import React from 'react';
import {Client} from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import {
    extractLinkedWikidataEntitiesForDelta,
    extractPlainTextFromContent,
} from '../extractEntitiesFromContent';
import {
    claimGroupsFromPartialClusters,
    mergePartialClaimGroupsByClaimKey,
    normalizeDeltaClusterApiPayload,
    normalizePartialClaimGroupsRaw,
    startReportDeltaClusters,
} from '../../../../../utils/api/deltas';
import buildHeaders from '../../../../../utils/api/fetchHeaders';
import {getAwpBackendOrigin} from '../../../../../config/apiBase';

const DELTA_CLUSTER_SOCKJS_TRANSPORTS = ['xhr-streaming', 'xhr-polling'];

function newDeltaJobId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `dc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function stompConnectHeaders() {
    const h = buildHeaders({});
    return Object.fromEntries(Object.entries(h).map(([k, v]) => [k, v != null ? String(v) : '']));
}

/**
 * Fetches report-aware delta clusters via async job + STOMP ({@code POST .../delta-clusters/start}, topic {@code /topic/delta-cluster/{jobId}}).
 * Sends plain report text plus top linked Wikidata entities (required by the API; capped for latency).
 */
export default function useReportDeltaClusters({content, publishedAt, reportId, deltaMode, deltaRunEpoch = 0, enabled = true}) {
    const [claimGroups, setClaimGroups] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [hasReportText, setHasReportText] = React.useState(false);
    const [claimDeltaStage, setClaimDeltaStage] = React.useState(null);
    const [claimDeltaMessage, setClaimDeltaMessage] = React.useState(null);
    const [claimUpdateProgressPercent, setClaimUpdateProgressPercent] = React.useState(null);
    const [segmentClaimBatchIndex, setSegmentClaimBatchIndex] = React.useState(null);
    const [segmentClaimBatchTotal, setSegmentClaimBatchTotal] = React.useState(null);

    React.useEffect(() => {
        if (!enabled) {
            setClaimGroups([]);
            setHasReportText(false);
            setError(null);
            setLoading(false);
            setClaimDeltaStage(null);
            setClaimDeltaMessage(null);
            setClaimUpdateProgressPercent(null);
            setSegmentClaimBatchIndex(null);
            setSegmentClaimBatchTotal(null);
            return undefined;
        }

        if (!content) {
            setClaimGroups([]);
            setHasReportText(false);
            setError(null);
            setLoading(false);
            setClaimDeltaStage(null);
            setClaimDeltaMessage(null);
            setClaimUpdateProgressPercent(null);
            setSegmentClaimBatchIndex(null);
            setSegmentClaimBatchTotal(null);
            return undefined;
        }

        const reportText = extractPlainTextFromContent(content);
        const entities = extractLinkedWikidataEntitiesForDelta(content);

        setHasReportText(Boolean(reportText && reportText.trim()));

        if (!publishedAt || !reportText.trim()) {
            setClaimGroups([]);
            setError(null);
            setLoading(false);
            setClaimDeltaStage(null);
            setClaimDeltaMessage(null);
            setClaimUpdateProgressPercent(null);
            setSegmentClaimBatchIndex(null);
            setSegmentClaimBatchTotal(null);
            return undefined;
        }

        if (entities.length === 0) {
            setClaimGroups([]);
            setError(null);
            setLoading(false);
            setClaimDeltaStage(null);
            setClaimDeltaMessage(null);
            setClaimUpdateProgressPercent(null);
            setSegmentClaimBatchIndex(null);
            setSegmentClaimBatchTotal(null);
            return undefined;
        }

        setLoading(true);
        setError(null);
        setClaimGroups([]);
        setClaimDeltaStage(null);
        setClaimDeltaMessage(null);
        setClaimUpdateProgressPercent(null);
        setSegmentClaimBatchIndex(null);
        setSegmentClaimBatchTotal(null);

        let cancelled = false;
        let stompSubscription = null;
        /** @type {Client | null} */
        let stompClient = null;

        const jobId = newDeltaJobId();
        const topic = `/topic/delta-cluster/${jobId}`;
        const wsUrl = `${getAwpBackendOrigin()}/ws`;
        const connectHeaders = stompConnectHeaders();

        function tearDownStomp() {
            try {
                stompSubscription?.unsubscribe();
            } catch (_) {
                /* ignore */
            }
            stompSubscription = null;
            try {
                stompClient?.deactivate();
            } catch (_) {
                /* ignore */
            }
            stompClient = null;
        }

        function handleStompMessage(rawBody) {
            if (cancelled) return;
            let msg;
            try {
                msg = rawBody ? JSON.parse(rawBody) : {};
            } catch {
                return;
            }
            const stage = msg.stage;
            if (stage) {
                setClaimDeltaStage(stage);
            }
            if (msg.message != null && String(msg.message).trim()) {
                setClaimDeltaMessage(String(msg.message).trim());
            }
            if (msg.progress != null && Number.isFinite(Number(msg.progress))) {
                setClaimUpdateProgressPercent(Math.round(Number(msg.progress) * 100));
            }
            if (msg.segmentClaimBatchIndex != null) {
                setSegmentClaimBatchIndex(msg.segmentClaimBatchIndex);
            }
            if (msg.segmentClaimBatchTotal != null) {
                setSegmentClaimBatchTotal(msg.segmentClaimBatchTotal);
            }

            if (stage === 'ERROR') {
                const errText = msg.error || msg.message || 'Delta cluster job failed.';
                console.error('[DeltaPanel] delta-cluster job error:', errText);
                setError(errText);
                setClaimGroups([]);
                setLoading(false);
                tearDownStomp();
                return;
            }
            if (stage === 'CACHED' || stage === 'COMPLETE') {
                const normalized = normalizeDeltaClusterApiPayload(msg.result);
                setClaimGroups(Array.isArray(normalized.claimGroups) ? normalized.claimGroups : []);
                setLoading(false);
                tearDownStomp();
                return;
            }
            if (stage === 'PARTIAL' && Array.isArray(msg.partialClusters) && msg.partialClusters.length > 0) {
                const partialGroups = claimGroupsFromPartialClusters(msg.partialClusters);
                if (partialGroups.length > 0) {
                    setClaimGroups((prev) => mergePartialClaimGroupsByClaimKey(prev, partialGroups));
                }
                return;
            }
            // Legacy production stage names (kept for backward compatibility).
            if (stage === 'LOOKING_FOR_CLAIM_UPDATES' && Array.isArray(msg.partialClaimGroups) && msg.partialClaimGroups.length > 0) {
                const normalizedPartials = normalizePartialClaimGroupsRaw(msg.partialClaimGroups);
                setClaimGroups((prev) => mergePartialClaimGroupsByClaimKey(prev, normalizedPartials));
            }
        }

        (async () => {
            try {
                await new Promise((resolve, reject) => {
                    stompClient = new Client({
                        webSocketFactory: () =>
                            new SockJS(wsUrl, null, {
                                transports: DELTA_CLUSTER_SOCKJS_TRANSPORTS,
                                transportOptions: {
                                    'xhr-streaming': {headers: {...connectHeaders}},
                                    'xhr-polling': {headers: {...connectHeaders}},
                                },
                            }),
                        connectHeaders,
                        reconnectDelay: 0,
                        onConnect: () => {
                            if (cancelled) {
                                tearDownStomp();
                                resolve();
                                return;
                            }
                            stompSubscription = stompClient.subscribe(topic, (message) => {
                                handleStompMessage(message?.body);
                            });
                            resolve();
                        },
                        onStompError: (frame) => {
                            reject(new Error(frame?.headers?.message || frame?.body || 'STOMP connection error'));
                        },
                    });
                    stompClient.activate();
                });

                if (cancelled) {
                    tearDownStomp();
                    return;
                }

                await startReportDeltaClusters({
                    publishedAt,
                    entities,
                    reportText,
                    reportId,
                    deltaMode,
                    jobId,
                });
            } catch (err) {
                if (cancelled) return;
                console.error('[DeltaPanel] Failed to run report delta clusters job:', err);
                const msg = err?.message || err?.response?.data?.message || err?.response?.data?.error;
                setError(msg && String(msg).trim() ? String(msg) : 'Failed to load claim deltas.');
                setClaimGroups([]);
                setLoading(false);
                tearDownStomp();
            }
        })();

        return () => {
            cancelled = true;
            tearDownStomp();
        };
    }, [content, publishedAt, reportId, deltaMode, deltaRunEpoch, enabled]);

    return {
        claimGroups,
        clustersLoading: loading,
        clustersError: error,
        hasReportText,
        claimDeltaStage,
        claimDeltaMessage,
        claimUpdateProgressPercent,
        segmentClaimBatchIndex,
        segmentClaimBatchTotal,
    };
}
